import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button } from '@material-ui/core';
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

import { history } from '../../../store';
import {
  OVERLAY_PANEL_WIDTH,
  OVERLAY_BOTTOM_SAFE_AREA_VARIABLE,
  RETURN_BUTTONS_CLEARANCE,
} from '../../pages/Shoreline/theme';

/**
 * @function usePublishBottomSafeArea
 * @description Measures how much room the floating button pair actually takes at the bottom of
 * the page and publishes it on the page wrapper as {@link OVERLAY_BOTTOM_SAFE_AREA_VARIABLE}, for
 * the overlay columns to cap their height with.
 * The reserve used to be a constant, and kept being slightly wrong: the buttons are laid out from
 * the *bottom* of the page wrapper while the columns are capped as a fraction of a box that is
 * not always that same wrapper - some modules measure against the overlay, others against `100vh`
 * - so at the resolutions where those two boxes disagreed (the navigation bar's height, typically)
 * the reserve came up short and the last panel slid under the buttons. Whatever the button text
 * wraps to in a given language, and whatever the browser's font size, also changes the pair's real
 * height, which no constant can follow.
 * Measuring resolves all of that at once: the value written here is the distance from the bottom
 * of the wrapper up to the top of the buttons, plus {@link RETURN_BUTTONS_CLEARANCE}, taken from
 * the same box the columns resolve their percentages against.
 * Re-measured on every resize of either the buttons or the wrapper, so it follows a window resize
 * and a language change alike.
 * @param {Object} ref - Ref to the button pair's own element.
 * @param {Boolean} enabled - Whether the pair is floating; an inline pair reserves nothing.
 * **Called by:
 * @see {ReturnButtons}
 * @memberof module:Core
 * @returns {void}
 */
const usePublishBottomSafeArea = (ref, enabled) => {
  useLayoutEffect(() => {
    const buttons = ref.current;
    const wrapper = buttons?.offsetParent;
    if (!enabled || !buttons || !wrapper) return;

    const publish = () => {
      const distanceFromBottom = wrapper.getBoundingClientRect().bottom - buttons.getBoundingClientRect().top;
      wrapper.style.setProperty(
        OVERLAY_BOTTOM_SAFE_AREA_VARIABLE,
        `${Math.ceil(distanceFromBottom) + RETURN_BUTTONS_CLEARANCE}px`,
      );
    };

    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(buttons);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [ref, enabled]);
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the standardized
 * "return" button pair shown on every module's result page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    maxWidth: '100%',
  },
  floating: {
    position: 'absolute',
    left: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 1002,
    pointerEvents: 'auto',
  },
}));

/**
 * @function ReturnButtons
 * @description Standardized pair of navigation buttons shown on every module's result page: one
 * to go back to that module's last acquisition step, one to return to the module-selection
 * screen. Consolidates the per-module "returnToLastStep"/"returnToBegin" button pairs, which had
 * drifted apart in color, styling, and navigation method across modules, into a single component.
 * @param {Object} props
 * @param {String} props.lastStepPath - Route to the module's last acquisition step (e.g. `/main/bathymetry/4`).
 * @param {String} [props.beginPath='/main/selection'] - Route to navigate to on "back to start".
 * @param {Function} [props.onBeforeReturnToBegin] - Optional side effect run right before
 * navigating to `beginPath` (e.g. ShoreS clearing its cached selected dates).
 * Sized to {@link OVERLAY_PANEL_WIDTH} so the pair lines up with the panels stacked above it in
 * the same column, rather than each module's buttons running to their own width.
 * Anchors itself to the bottom-left corner of the page by default, the standard placement on
 * every module result page. This relies on being rendered as a direct child of the page's root
 * wrapper (which every module gives `position: relative`) rather than nested inside one of the
 * overlay columns - an absolutely positioned element resolves against its nearest positioned
 * ancestor, so nesting it inside a panel would pin it to that panel instead of to the page.
 * @param {String} [props.className] - Optional extra class applied to the wrapping container, for
 * pages that need custom positioning (e.g. a floating overlay on a full-screen map).
 * @param {Boolean} [props.floating=true] - Whether to pin the pair to the page's bottom-left
 *  corner. Pass `false` to place it inline, in normal document flow.
 * @param {Number} [props.width] - Width of the pair, in px. Defaults to {@link OVERLAY_PANEL_WIDTH}
 *  so it matches the panels stacked above it; a module whose left column is deliberately wider
 *  (CSqueeze and EUSCoast carry charts and tables there) passes its own column width instead.
 * @param {String} [props.buttonClassName] - Optional extra class applied to both buttons, for
 * pages that need a custom size (e.g. full-width buttons inside a card).
 * @param {Number} [props.marginLeft=0] - Left offset, in pixels, so the buttons line up with
 * whatever else sits on the left of that specific page (this varies per module - each page's
 * own left-aligned siblings use a different inset - so it's passed in rather than hardcoded here).
 * **Called by:
 * @see {BathymetryResult}, {MepBayResult}, {ShorelineResult}, {IntertidalResult}, {ShoreRResult}, {ShoreSResult}, {CSqueezeResult}
 * @memberof module:Core
 * @returns {JSX.Element} The two standardized return-navigation buttons.
 */
const ReturnButtons = ({
  lastStepPath,
  beginPath = '/main/selection',
  onBeforeReturnToBegin,
  className,
  buttonClassName,
  marginLeft = 0,
  floating = true,
  width = OVERLAY_PANEL_WIDTH,
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const ref = useRef(null);
  usePublishBottomSafeArea(ref, floating);

  const returnToLastStep = () => history.push(lastStepPath);

  const returnToBegin = () => {
    if (onBeforeReturnToBegin) onBeforeReturnToBegin();
    history.push(beginPath);
  };

  return (
    <Box
      ref={ref}
      className={[classes.wrapper, floating && classes.floating, className].filter(Boolean).join(' ')}
      style={{ marginLeft, width }}>
      <Button
        className={buttonClassName}
        color="primary"
        variant="contained"
        startIcon={<ArrowBackOutlinedIcon />}
        onClick={returnToLastStep}>
        {t('forms.core.returnButtons.returnToLastStep')}
      </Button>
      <Button
        className={buttonClassName}
        color="secondary"
        variant="contained"
        startIcon={<HomeOutlinedIcon />}
        onClick={returnToBegin}>
        {t('forms.core.returnButtons.returnToBegin')}
      </Button>
    </Box>
  );
};

export default ReturnButtons;
